import type {
  StructuredContent,
  StructuredContentDocument,
} from '@repo/supergraph/structuredContent/types';
import { isStructuredContent } from '@repo/supergraph/structuredContent/utils';
import _ from 'lodash';
import * as v from 'valibot';
import { getChildrenHeadings } from '../TableOfContents/utils';
import { marketplaceFieldSCSchema } from './components/MarketplaceField/schema';

export const extractNodeText = (node: StructuredContent): string | undefined => {
  if (_.isNull(node) || _.isUndefined(node)) return;
  if (_.isNumber(node)) return `${node}`;
  if (_.isString(node)) return node;
  if (_.isArray(node)) {
    return _.reduce(node, (acc, val) => acc + extractNodeText(val), '');
  }
};

export const getAllChildrenStrings = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string => {
  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') {
        prevValue = prevValue.concat(child);
      }
      if (isStructuredContent(child)) {
        const isMarketplaceFieldSC = v.safeParse(marketplaceFieldSCSchema, child.props);
        if (isMarketplaceFieldSC.success) {
          const { marketplaceField } = isMarketplaceFieldSC.output;
          prevValue = prevValue.concat(marketplaceField.value);
        } else {
          prevValue = getAllChildrenStrings(child, prevValue);
        }
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue = getAllChildrenStrings(sc?.props?.children, prevValue);
  }
  return prevValue;
};

/**
 * Groups structured content nodes into sections based on level 2 headings (H2).
 *
 * This function takes a `StructuredContentDocument` and wraps consecutive nodes that follow
 * each H2 heading inside a `Container`.
 *
 * ## Example Transformation:
 * Input:
 *   - H2
 *   - Paragraph
 *   - Image
 *   - H2
 *   - Paragraph
 *
 * Output:
 *   - Container (H2, Paragraph, Image)
 *   - Container (H2, Paragraph)
 *
 * @param doc - A structured content document to be transformed.
 * @param excludeHeadings - Optional array of heading strings to exclude (case-insensitive exact match).
 *                          Sections with these headings will be completely omitted from the output.
 * @returns A new document with sections grouped under H2s, each wrapped in a `Container`.
 */
export function groupedByH2ContentAndWrapItWithContainer(
  doc: StructuredContentDocument<Record<string, unknown>>,
  excludeHeadings?: string[],
): StructuredContent {
  // Return early if the document is not well-formed
  if (doc?.type !== 'Document' || !Array.isArray(doc?.props?.children)) return doc;

  const wrappedChildren: StructuredContent[] = [];
  let currentSection;
  let isExcludedSection = false;

  for (const child of doc.props.children) {
    if (!isStructuredContent(child)) {
      wrappedChildren.push(child);
      continue;
    }

    const isH2 =
      child.type === 'Container' &&
      Array.isArray(child.props?.children) &&
      isStructuredContent(child.props.children[0]) &&
      child.props.children[0].type === 'ContentHeading' &&
      child.props.children[0].props?.level === 2;

    if (isH2) {
      // Close out the previous section, if one exists and is not excluded
      if (currentSection && !isExcludedSection) {
        wrappedChildren.push({
          type: 'ContentSection',
          version: '1.0.0',
          props: {
            spacing: { bottom: 3 },
            children: currentSection,
          },
        });
      }

      // Check if this H2 should be excluded (case-insensitive exact match)
      const headingText = getAllChildrenStrings(child).toLowerCase();
      isExcludedSection = !!excludeHeadings?.some(excluded =>
        headingText === excluded.toLowerCase(),
      );

      // Start a new section (will be skipped if excluded)
      currentSection = isExcludedSection ? undefined : [child];
    } else if (currentSection && !isExcludedSection) {
      // Continue the current section (only if not excluded)
      currentSection.push(child);
    } else if (!currentSection && !isExcludedSection) {
      // No section started and not excluded, just push node directly
      wrappedChildren.push(child);
    }
    // If isExcludedSection is true, we skip this child entirely
  }

  // Push the final section if it exists and is not excluded
  if (currentSection && !isExcludedSection) {
    wrappedChildren.push({
      type: 'ContentSection',
      version: '1.0.0',
      props: {
        spacing: { bottom: 3 },
        children: currentSection,
      },
    });
  }

  return {
    ...doc,
    props: {
      ...doc.props,
      children: wrappedChildren,
    },
  };
}

export const getChildHeadingId = (doc: StructuredContentDocument) => {
  const heading = getChildrenHeadings(doc)[0];
  return heading.id;
};

/**
 * Injects overallRating into StarRatingCategories components within structured content
 * @param doc - Structured content document to transform
 * @param overallRating - The rating value to inject
 * @returns A new document with StarRatingCategories components updated
 */
export function injectRatingIntoStarRatingCategories(
  doc: StructuredContent | undefined,
  overallRating?: number,
): StructuredContent {
  if (!doc || !overallRating) {
    return doc;
  }

  if (!isStructuredContent(doc)) {
    return doc;
  }

  // Update StarRatingCategories node with overallRating
  if (doc.type === 'StarRatingCategories') {
    return {
      ...doc,
      props: {
        ...doc.props,
        overallRating,
      },
    };
  }

  // Recursively process children
  if (doc.props?.children) {
    const children = Array.isArray(doc.props.children)
      ? doc.props.children
      : [doc.props.children];

    const updatedChildren = children.map(child =>
      injectRatingIntoStarRatingCategories(child, overallRating),
    );

    return {
      ...doc,
      props: {
        ...doc.props,
        children: Array.isArray(doc.props.children) ? updatedChildren : updatedChildren[0],
      },
    };
  }

  return doc;
}
